import _ from 'lodash';
import polybool from 'polybooljs';
import emitter from '../emitter';
import geometry from './util/geometry';
import productsService from '../services/products';
import swiperService from '../services/swiper';
import { floor, round, ceil } from '../util/round';
import constants from '../settings/constants';
import { TRASH_CAN_HOVER, ITEM_MOVING } from '../settings/events';
import tacHelpers from '../state/tac/tacHelpers';
import store from '../state';
import {
  isStandAlone,
  isFloorStanding,
  isWallMounted,
} from '../services/products/models';
import { range } from '../state/tac/range';
import { snapPadding } from './util/snapPadding';
import SceneBase from './SceneBase';
import idGenerator from '../util/aactools/idGenerator';
import {
  supersection_drawSuggestions,
  supersection_relativePos,
  supersection_recalc,
} from './util/supersection';
import { selectCuttableMountingRailHintCheckPending } from '../state/popups/popupsSelectors';
import { thunkHandleMountingRailPopup } from '../state/popups/popupsThunks';
import {
  thunkAddItem,
  thunkUpdateItem,
  thunkUpdateMultiple,
} from '../state/tac/tacThunks';
import { ITEMS } from '../constants';

export default class Scene extends SceneBase {
  GRID_TYPES = {
    DYNAMIC_GRID: 'DYNAMIC_GRID',
    GRID: 'GRID',
  };

  // calc dragmode and slots
  buildDragEnv(item, slotItem, fitItem, draw = false) {
    draw && this.rectContainer.removeChildren();
    let slots;
    let mode;
    let rects;
    let space = this.room.getSpace(this.tac);

    const dependentItems = range.getDependentItems(item, this.tac);
    const fittingTac = tacHelpers.filterTac(
      this.tac,
      dependentItems.map(item => item.itemid)
    );

    const slotOptions = {
      extendabilityLocked: slotItem.logic?.extendabilityLockedOnDrag,
    };

    const parentalSlots = tacHelpers.getOpenSlots(
      fittingTac,
      slotItem,
      slotOptions
    );
    if (parentalSlots.length) {
      this.itemContainer.drawDropAreas(parentalSlots);
    }

    if (isStandAlone(item)) {
      rects = tacHelpers.getRects(fittingTac, fitItem, this.tac);
      space = tacHelpers.getSpaceForItem(space, item);
      let roomSlots = tacHelpers.getRoomSlots(
        space,
        rects,
        fitItem,
        slotItem,
        this.tac
      );
      draw && this.__debug__drawRects(roomSlots);
      if (
        productsService.isSection(item) ||
        productsService.isFrame(item) ||
        productsService.isShelvingUnit(item)
      ) {
        // something with making addonshelves on dragged item
        // be a part of the colliding item
        roomSlots = roomSlots
          .map(slot =>
            tacHelpers.modifySlot(
              slot,
              fitItem,
              rects,
              this.room.model,
              this.itemContainer.origo
            )
          )
          .filter(Boolean);
      }

      slots = roomSlots.concat(parentalSlots);
      mode = parentalSlots.length
        ? constants.DRAG_MODE.MIXED
        : constants.DRAG_MODE.FLOAT;
    } else if (tacHelpers.isSuperSectionHandle(item)) {
      mode = constants.DRAG_MODE.MULTI;
    } else {
      slots = parentalSlots;
      mode = constants.DRAG_MODE.INSERT;
    }

    draw && this.__debug__drawRects(slots, 0xff0000);

    return { slots, mode, space, rects };
  }

  onDragStart({ mode, sprite, mouse, offset, domSize }) {
    if (this.dragging) {
      return;
    }
    const state = store.getState();
    const { userAgent, scene, popups } = state;
    const tac = state.tac.present;

    this.tac = tac;
    this.itemContainerOptions = {
      isMobile: userAgent.isMobile,
      isPortrait: userAgent.isPortrait,
      measurementsActive: scene.measurementsActive,
      errorVisible: popups.sceneErrorVisible,
      introPopupsVisible: popups.introPopupsVisible,
      hasShownPegboardHint: popups.hasShownPegboardHint,
      popups: popups.scenePopups,
      firstRender: !this.hasRendered,
      showPropping: scene.showPropping,
    };

    const item = sprite.item;

    const fitItem = this.withoutClothesRails(item, this.crFilters.hidden).item;

    this.args.onDragStart({ mode, sprite, item });
    const naked = this.withoutClothesRails(item, this.crFilters.all);

    const env = this.buildDragEnv(item, naked.item, fitItem);
    let slots;

    if (naked.removed.length && item.itemid) {
      const nakedEnv = this.buildDragEnv(item, naked.item, naked.item);
      const gItemPos = tacHelpers.getGlobalCoords(item, this.tac);
      const gItem = { ...fitItem, ...gItemPos };
      const currentSlot = geometry.closestCollidingRect(gItem, env.slots);

      const mergedSlots = currentSlot
        ? nakedEnv.slots
            .filter(slot => !geometry.contains(slot, currentSlot))
            .concat(currentSlot)
        : nakedEnv.slots;

      slots = mergedSlots;
    } else {
      slots = env.slots;
    }

    const rects = env.rects;
    mode = env.mode;

    if (mode & constants.DRAG_MODE.MIXED) {
      if (slots.length === 0) {
        if (!sprite.parent) {
          sprite.destroy();
        }

        return;
      }
      const newItem = { ...item };

      if (sprite.parent !== this.itemContainer) {
        if (sprite.parentItem && sprite.parentItem !== this.room.model) {
          offset.x += this.itemContainer.x;
          offset.y += this.itemContainer.y;

          newItem.x += sprite.parentItem.x || 0;
          newItem.y += sprite.parentItem.y || 0;

          sprite.x = mouse.x - offset.x;
          sprite.y = mouse.y - offset.y;
        }

        this.itemContainer.addChild(sprite, false);
        this.itemContainer.sortChildrenDragging(sprite, newItem, mode);
      }

      this.dragging = {
        item: newItem,
        originalItem: item,
        mode,
        offset,
        slots,
        space: env.space,
        sprite,
        domSize: domSize,
        rects,
      };
    } else if (mode === constants.DRAG_MODE.MULTI) {
      const ss = tacHelpers.findSuperSection(this.tac, item);

      const slotPolygon = tacHelpers.getSuperSectionSpace(this.tac, ss);

      this.dragging = {
        item: { ...item },
        supersection: _.cloneDeep(ss),
        originalItem: item,
        sprites: ss
          .filter(item => !productsService.isType(item, 'mounting-rail'))
          .map(item => this.getSprite(item.itemid)),
        sprite, // keeping this for positioning
        slotPolygon,
        mode,
        offset,
        space: env.space,
        domSize: domSize,
        rects,
      };
    }

    this.onDragMove({
      data: {
        global: mouse,
      },
    });
  }

  getNewItemTrashcanSizeScale() {
    const { domSize, sprite } = this.dragging;
    const { data, item } = sprite;
    const { ratio } = this.room;
    const zoom = swiperService.getTrashcanZoomLevel(item);

    if (sprite.width && sprite.height) {
      return sprite.width >= sprite.height
        ? (domSize.width * zoom) / (sprite.width / sprite.scale.x)
        : (domSize.height * zoom) / (sprite.height / sprite.scale.y);
    }
    return data.outerSize.width >= data.outerSize.height
      ? (domSize.width * zoom) / (data.outerSize.width * ratio)
      : (domSize.height * zoom) / (data.outerSize.height * ratio);
  }

  /**
   * Calculates and updates the scene according to the new mouse position.
   * Triggered every time the user moves while dragging.
   * @param {*} e The mouse event;
   * @returns {undefined}
   */
  onDragMove(e) {
    if (!this.dragging) {
      return;
    }

    if (e.data.originalEvent && e.data.originalEvent.cancelable) {
      e.data.originalEvent.preventDefault();
    }

    this.dragging.lastDragEventPos = {
      data: { global: e && e.data && e.data.global },
    };

    const { mode, slots } = this.dragging;
    const { model: room, ratio } = this.room;
    const options = {
      wallResizerActive: this.wallResizerActive,
      measurementsActive: this.measurementsActive,
      showPropping: this.itemContainerOptions.showPropping,
    };

    if (mode & constants.DRAG_MODE.MIXED) {
      const { offset, sprite } = this.dragging;
      const item = sprite.item;
      sprite.update({
        item: item,
        options,
      });

      const { data } = sprite;
      const defaultSizeScale = 1;
      const inTrash = this.isInTrashcan(e);

      emitter.emit(TRASH_CAN_HOVER, inTrash, !!item.itemid);

      let sizeScale = defaultSizeScale;
      if (inTrash && !item.itemid) {
        sizeScale = this.getNewItemTrashcanSizeScale();
      }

      const newPos = this.calcPos(data, e, offset, ratio, room);
      this.fitToGrid(newPos, mode, slots, round);

      const newItem = {
        ...item,
        x: newPos.x + this.itemContainer.origo.x,
        y: newPos.y,
        z: newPos.z,
      };

      if (mode & constants.DRAG_MODE.INSERT) {
        const collidingSlots = geometry
          .getCollidingRects(newItem, slots, snapPadding(mode, newItem))
          .filter(slot => !!slot.parent);

        if (collidingSlots.length > 0) {
          const slot = geometry.closestCollidingRect(
            geometry.pad(newItem, snapPadding(mode, newItem)),
            collidingSlots
          );

          Object.assign(newItem, {
            x: slot.x,
            y: slot.y,
            z: slot.z,
            width: slot.width,
            height: slot.height,
            depth: slot.depth,
          });

          if (
            sprite.parent === this.itemContainer ||
            newItem.x !== item.x ||
            newItem.y !== item.y ||
            tacHelpers.getParent(this.tac, item)?.itemid !== slot.parent.itemid
          ) {
            const parentSprite = this.getSprite(slot.parent.itemid);
            sizeScale = defaultSizeScale;
            sprite.scale.set(sizeScale);

            parentSprite.addSprite(sprite, slot, this.tac);

            this.adaptProppings(sprite.item, sprite);
            this.dragging.item = newItem;

            sprite.updateDependentItemsDragging &&
              sprite.updateDependentItemsDragging(sprite.item, slot.parent);
            this.render();
          }
          emitter.emit(ITEM_MOVING, this.tac, newItem);
          return;
        } else if (sprite.parent !== this.itemContainer) {
          const oldParent = this.itemContainer.getSprite(
            sprite.parentItem.itemid
          );

          const currentPos = {
            x: this.dragging.item.x,
            y: this.dragging.item.y,
            z: this.dragging.item.z,
          };
          Object.assign(
            this.dragging.item,
            this.dragging.originalItem,
            currentPos
          );
          sprite.update({
            item: this.dragging.item,
            options,
            parentItem: room,
          });
          this.itemContainer.addChild(sprite);

          if (oldParent && oldParent.adaptPropping) {
            oldParent.adaptPropping();
          }

          emitter.emit(ITEM_MOVING, this.tac, sprite.item);
        }
      }
      if (
        this.isInScene(e) &&
        !this.isInTrashcan(e) &&
        mode & constants.DRAG_MODE.FLOAT
      ) {
        if (mode & constants.DRAG_MODE.INSERT) {
          Object.assign(newItem, this.dragging.originalItem, {
            x: newItem.x,
            y: newItem.y,
            z: newItem.z,
            items:
              newItem.id === this.dragging.originalItem.id
                ? newItem.items
                : this.dragging.originalItem.items,
          });
          sprite.update({
            item: newItem,
            parentItem: room,
            options,
          });
        }

        if (isFloorStanding(item) && !isWallMounted(item)) {
          newItem.y = this.getSnapToFloorYPosition(item);
        }

        if (!slots.some(slot => geometry.contains(slot, newItem))) {
          if (mode === constants.DRAG_MODE.INSERT) {
            const collidingSlots = geometry.getCollidingRects(
              newItem,
              slots,
              snapPadding(mode, newItem)
            );
            if (collidingSlots.length !== 1) {
              return;
            }
          }

          Object.assign(
            newItem,
            geometry.closestPosition(
              newItem,
              slots.filter(slot => !slot.parent)
            )
          );
        }

        if (isWallMounted(newItem) && isFloorStanding(newItem)) {
          if (this.isPlacedOnSkirt(newItem)) {
            this.snapToSkirtOrFloor(newItem);
          }
        }
        this.fitToGrid(newItem, mode, slots);
        this.snapToPartner(newItem);
      }

      sprite.scale.set(sizeScale);
      sprite.x =
        (newItem.x - this.itemContainer.origo.x) * ratio -
        (data.scaledVertices[4][0] - data.scaledVertices[0][0]);
      sprite.y = (room.height - (data.faceSize.height + newItem.y)) * ratio;

      sprite.x += (sprite.width / sizeScale - sprite.width) / 2;
      sprite.y += (sprite.height / sizeScale - sprite.height) / 2;

      this.adaptProppings(newItem, sprite);
      this.dragging.item = newItem;
      if (sprite.updateDependentItemsDragging && this.hasMoved(newItem)) {
        sprite.updateDependentItemsDragging(newItem, sprite.parentItem);
        if (productsService.isType(newItem, ITEMS.SHELF_DRAWER)) {
          this.itemContainer.sortChildrenDragging(sprite, newItem, mode);
        }
      } else if (!sprite.updateDependentItemsDragging) {
        this.itemContainer.sortChildrenDragging(sprite, newItem, mode);
      }

      emitter.emit(ITEM_MOVING, this.tac, newItem);
    } else if (mode === constants.DRAG_MODE.MULTI) {
      const {
        supersection,
        slotPolygon,
        offset,
        item: grabbedItem,
      } = this.dragging;
      const data = this.dragging.sprite.data;

      const newPos = this.calcPos(data, e, offset, ratio, room);
      this.fitToGrid(newPos, constants.DRAG_MODE.FLOAT, [], round);

      const newItem = {
        ...grabbedItem,
        x: newPos.x + this.itemContainer.origo.x,
        y: newPos.y,
      };

      if (tacHelpers.hasTable({ items: supersection })) {
        newItem.y = grabbedItem.y;
      }

      if (this.hasMoved(newItem)) {
        const supersection = this.dragging.sprites.map(sprite => sprite.item);

        const recalced = supersection_recalc(
          supersection,
          grabbedItem,
          newItem,
          this,
          {
            lockedY: tacHelpers.hasTable({ items: supersection }),
          }
        );

        Object.assign(newItem, recalced.item);

        const ssPoly = geometry.rects2poly(recalced.items);
        if (polybool.intersect(ssPoly, slotPolygon).regions.length) {
          // ss collides with the walls or something on the scene, don't move it
          return;
        }

        const movedSupersection = supersection.map(item => ({
          ...item,
          ...supersection_relativePos(item, grabbedItem, newItem),
        }));

        supersection_drawSuggestions(movedSupersection, this);
      }
      this.dragging.item = newItem;
    }

    this.dragging.previousEventItem = Object.assign({}, this.dragging.item);

    this.render();
  }

  onDragEnd(e) {
    this.rectContainer.removeChildren();
    this.args.onDragEnd && this.args.onDragEnd();

    if (!this.dragging) {
      const cuttableMountingRailHintCheckPending =
        selectCuttableMountingRailHintCheckPending(store.getState());
      cuttableMountingRailHintCheckPending &&
        store.dispatch(
          thunkHandleMountingRailPopup({ omitExtendedConfClosedCheck: true })
        );
      return;
    }

    const { item, mode, slots, sprite } = this.dragging;

    sprite.clearCache && sprite.clearCache();

    if (mode & constants.DRAG_MODE.MIXED) {
      const noCrs = this.withoutClothesRails(item, this.crFilters.hidden);
      item.items = noCrs.item.items;

      if (
        (this.isInTrashcan(e) ||
          this.isInTrashcan(this.dragging.lastDragEventPos)) &&
        !e.abort
      ) {
        sprite.destroy();
        const originalItem = { ...this?.dragging?.originalItem };
        this.dragging = null;
        if (idGenerator.hasRealId(item) || item.forceRemoval) {
          const origin = 'drag';
          this.removeItem(originalItem || item, { origin: origin });
        } else {
          this.update(true);
        }
        return;
      }

      const isInScene =
        this.isInScene(e) || this.isInScene(this.dragging.lastDragEventPos);

      this.dragging = null;

      if (sprite.dummy) {
        sprite.destroy();
      }

      // dropping between swiper and scene can cause items to appear off floor.
      // only applies to float and not mixed mode

      const badFloorDrop =
        !isWallMounted(item) &&
        mode === constants.DRAG_MODE.FLOAT &&
        item.y > constants.PAD_HEIGHT &&
        !item.items?.some(item => item.y < 0);

      // for fixed scene, left corner is 0,0 so no drops outside wall.
      const badDropOutsideWall = !tacHelpers.isWithinWall(item, this.tac, true);

      if (!isInScene || e.abort || badDropOutsideWall || badFloorDrop) {
        if (mode === constants.DRAG_MODE.MIXED && sprite.dummy) {
          sprite.destroy();
        }
        this.update(true);
      } else if (mode & constants.DRAG_MODE.INSERT) {
        let collidingSlots = geometry.getCollidingRects(item, slots);

        if (collidingSlots.length > 0) {
          if (collidingSlots.some(slot => slot.parent)) {
            collidingSlots = collidingSlots.filter(slot => !!slot.parent);
          }
          const slot = geometry.closestCollidingRect(item, collidingSlots);
          if (slot.parent) {
            item.x = slot.x;
            item.y = slot.y;
            item.z = slot.z;
            item.width = slot.width;
            item.height = slot.height;
            item.depth = slot.depth;
          }

          const fittingProduct =
            productsService.getFit(item, {
              ...slot,
              width: item.width,
              depth: item.depth,
              height: item.height,
              color: item.filter.color,
            }) || productsService.getProduct(item.id);
          const fullFit = tacHelpers.getSwitchableItem(item, fittingProduct);
          const parentSprite =
            slot.parent && this.itemContainer.getSprite(slot.parent.itemid);
          const fosterParent =
            parentSprite &&
            parentSprite.getChildDisplacement &&
            parentSprite.getChildDisplacement(slot);

          const offset = tacHelpers.getGlobalCoords(
            fosterParent || slot.parent,
            this.tac
          ) || {
            x: 0,
            y: 0,
            z: 0,
          };

          const newItem = {
            ...item,
            ...fittingProduct,
            ...fullFit,
            x: item.x - offset.x,
            y: item.y - offset.y,
            z: item.z - offset.z,
          };

          if (slot.partnerSlot?.parent) {
            newItem.connectsTo = slot.partnerSlot.parent;
          }
          if (idGenerator.hasRealId(newItem)) {
            store.dispatch(
              thunkUpdateItem(newItem, fosterParent || slot.parent)
            );
          } else {
            store.dispatch(thunkAddItem(newItem, fosterParent || slot.parent));
          }
        } else {
          this.update(true);
        }
      } else if (idGenerator.hasRealId(item)) {
        store.dispatch(thunkUpdateItem(item, undefined, { tac: this.tac }));
      } else {
        store.dispatch(thunkAddItem(item, undefined, { tac: this.tac }));
      }
    } else if (mode & constants.DRAG_MODE.MULTI) {
      const isInScene =
        this.isInScene(e) || this.isInScene(this.dragging.lastDragEvent);
      if (e.abort || !isInScene) {
        this.dragging = null;
        this.update(true);
      } else {
        const ssItems = this.dragging.sprites.map(sprite => sprite.item);
        this.dragging = null;
        store.dispatch(thunkUpdateMultiple(ssItems));
      }
    }
  }

  hasMoved(item) {
    return (
      !this.dragging.previousEventItem ||
      !geometry.contains(item, this.dragging.previousEventItem)
    );
  }

  calcPos(data, event, offset, ratio, room) {
    const pos = {
      x:
        data.projectedVertices[4][0] -
        data.projectedVertices[0][0] +
        (event.data.global.x - offset.x) / ratio,

      y:
        room.height -
        ((event.data.global.y - offset.y) / ratio + data.faceSize.height),
      z: productsService.getInitialZPos(this.dragging?.item),
    };
    return pos;
  }

  getGrid(mode, item) {
    const dynamicGrid = constants.DYNAMIC_GRID && constants.DYNAMIC_GRID[mode];
    const gridType = dynamicGrid
      ? this.GRID_TYPES.DYNAMIC_GRID
      : this.GRID_TYPES.GRID;

    if (!dynamicGrid) {
      return { ...constants.GRID, gridType };
    }

    const rects = this.dragging.rects;
    if (!item || !rects) {
      return { ...dynamicGrid, gridType };
    }

    const closeRects = geometry.getCollidingRects(item, rects, {
      right: dynamicGrid.x.activationDistance,
      left: dynamicGrid.x.activationDistance,
      top: 5,
      bottom: 5,
    });
    /*
      ditch any objects that are covered by other objects; we only snap
      to stuff that can spawn sections
    */

    const usefulRects = closeRects.filter(
      rect => !geometry.obscured(rect, item, closeRects)
    );
    const closest = geometry.closestCollidingRect(item, usefulRects);
    if (!closest) {
      return { ...constants.GRID, gridType };
    }

    function getAdjustedGrid(dynamicGrid, origin) {
      const adjustedGrid = {
        x: { ...dynamicGrid.x },
        y: { ...dynamicGrid.y },
      };
      adjustedGrid.x.offset = origin.x % adjustedGrid.x.step;
      adjustedGrid.y.offset = origin.y % adjustedGrid.y.step;
      return adjustedGrid;
    }

    const fitted = usefulRects
      .map(rect => getAdjustedGrid(dynamicGrid, rect))
      .map(grid => ({
        ...item,
        x: this.getClosestGridPos(item.x, mode, 'x', round, grid),
        y: this.getClosestGridPos(item.y, mode, 'y', round, grid),
        grid,
      }));

    const closestFit = fitted.reduce(
      (curr, cand) => {
        if (geometry.distance(item, cand) < geometry.distance(item, curr)) {
          return cand;
        }
        return curr;
      },
      { x: Infinity, y: Infinity }
    );

    return { ...closestFit.grid, gridType };
  }

  getClosestGridPos(val, mode, axis, adjust, grid) {
    grid = grid || this.getGrid(mode);

    if (grid[axis].offset < 0) {
      adjust = ceil;
    }

    return adjust(val - grid[axis].offset, grid[axis].step) + grid[axis].offset;
  }

  fitToGrid(newItem, mode, slots, adjust = floor) {
    let grid = this.getGrid(mode, newItem);
    const gridSlots = slots.filter(
      slot => slot.width >= grid.x.step && slot.height >= grid.y.step
    );

    newItem.x = this.getClosestGridPos(newItem.x, mode, 'x', round, grid);
    newItem.y = this.getClosestGridPos(newItem.y, mode, 'y', adjust, grid);

    // search for a slot this many steps away (total steps both x and y)
    // The BOAXEL Uprights needing more retries to ensure that they don't get placed on top of each other
    let retries =
      mode === constants.DRAG_MODE.FLOAT &&
      grid.gridType === this.GRID_TYPES.DYNAMIC_GRID
        ? 50
        : 5;
    while (
      !slots.some(slot => geometry.contains(slot, newItem)) &&
      this.dragging &&
      geometry.contains(this.dragging.space, newItem, {
        left: grid.x.step,
        right: grid.x.step,
        top: grid.y.step,
        bottom: grid.y.step,
      }) &&
      retries--
    ) {
      grid = this.getGrid(mode, newItem);
      const closestPos = geometry.closestPosition(newItem, gridSlots);

      if (closestPos) {
        if (closestPos.x < newItem.x) {
          newItem.x = this.getClosestGridPos(
            newItem.x - grid.x.step,
            mode,
            'x',
            adjust,
            grid
          );
        } else if (closestPos.x > newItem.x) {
          newItem.x = this.getClosestGridPos(
            newItem.x + grid.x.step,
            mode,
            'x',
            adjust,
            grid
          );
        }

        if (closestPos.y < newItem.y) {
          newItem.y = this.getClosestGridPos(
            newItem.y - grid.y.step,
            mode,
            'y',
            adjust,
            grid
          );
        } else if (closestPos.y > newItem.y) {
          newItem.y = this.getClosestGridPos(
            newItem.y + grid.y.step,
            mode,
            'y',
            adjust,
            grid
          );
        }
      }
    }
  }

  /**
   * Gets the y-position to snap the item to.
   * @param {Object} item
   * @returns {number} The y-position that the item should be placed at to make it stand on the floor.
   */
  getSnapToFloorYPosition(item) {
    /*
    If a floor-standing item has children with a negative y,
    we need to account for that offset when "flooring"
    */
    const negativeChild = item.items && item.items.find(item => item.y < 0);
    return negativeChild ? -negativeChild.y : 0;
  }

  /**
   * Snaps the item to a partner item.
   * @example A section is placed close to another section, if its close enough it will snap to the closest section/partner.
   * @param {Object} newItem
   */
  snapToPartner(newItem) {
    const snappingPosition = tacHelpers.getSnappingPosition(
      newItem,
      this.dragging.rects
    );
    if (snappingPosition) {
      Object.assign(newItem, snappingPosition);
    }
  }

  /**
   * Snaps the item if its placed on the skirt area to either;
   * 1) The floor - If the item is placed below half of the skirt.
   * 2) The top of the skirt - If the item is placed above half of the skirt.
   * @param {Object} newItem
   */
  snapToSkirtOrFloor(newItem) {
    const snappingPosition = tacHelpers.getSkirtSnappingPosition(
      newItem,
      this.dragging.rects
    );
    if (snappingPosition) {
      Object.assign(newItem, snappingPosition);
    }
  }
  /**
   * Checks to see if the item is placed on the skirt or not.
   * @param {Object} item
   * @returns {Boolean} A value depending on whether the item is placed on the skirt or not.
   */
  isPlacedOnSkirt(item) {
    return item.y < constants.SKIRT_HEIGHT;
  }
}
